import {Injectable} from '@angular/core';
import {HttpHeaders} from '@angular/common/http';
import {AuthStateService} from '@auth/states';
import {CompanyStateService} from '@core/states';
import {ApiCollection, ApiResponse, Attachment, GroupCourse, GroupItem, GroupMember, GroupNote, GroupPolicy} from '@core/types';
import {CONFIG, ENDPOINTS, GET_URL, log} from '@core/utils';
import {HttpService} from '@core/services';

@Injectable({
  providedIn: 'root'
})
export class GroupService {
  constructor(
    private apiService: HttpService,
    private companyState: CompanyStateService,
    private authState: AuthStateService
  ) {}

  async getGroups(options?: {skipLoader?: boolean}): Promise<ApiResponse<GroupItem[]>> {
    try {
      const url = GET_URL(ENDPOINTS.GROUPS);
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      const groups = this.apiService.normalizeCollection<any>(response).member.map((item: any) => this.mapGroup(item));
      return {success: true, result: groups};
    } catch (error: any) {
      log.error('Error loading groups:', error);
    }
    return {success: false, message: 'No se pudieron cargar los grupos.'};
  }

  async createGroup(payload: {
    name: string;
    description?: string;
  }, options?: {skipLoader?: boolean}): Promise<ApiResponse<GroupItem>> {
    try {
      const url = GET_URL(ENDPOINTS.GROUPS);
      const headers = this.buildHeaders(options?.skipLoader);
      const body = {
        name: payload.name.trim(),
        description: payload.description?.trim() || undefined
      };
      const response: any = await this.apiService.post(url, body, headers ? {headers} : undefined);
      return {success: true, result: this.mapGroup(response)};
    } catch (error: any) {
      log.error('Error creating group:', error);
      const message = this.extractValidationMessage(error);
      if (message) {
        return {success: false, message};
      }
    }
    return {success: false, message: 'No se pudo crear el grupo.'};
  }

  async addMembers(
    groupId: string | number,
    employeeIds: Array<string | number>,
    options?: {skipLoader?: boolean}
  ): Promise<ApiResponse<GroupItem>> {
    try {
      const normalizedIds = Array.from(new Set(employeeIds.filter(Boolean)));
      if (!normalizedIds.length) {
        return {success: true};
      }

      const url = GET_URL(ENDPOINTS.GROUP_MEMBERS, {id: groupId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.post(url, {employeeIds: normalizedIds}, headers ? {headers} : undefined);
      return {success: true, result: this.mapGroup(response)};
    } catch (error: any) {
      log.error('Error adding group members:', error);
      const message = this.extractValidationMessage(error);
      if (message) {
        return {success: false, message};
      }
    }
    return {success: false, message: 'No se pudieron asociar los empleados al grupo.'};
  }

  async updateGroup(
    groupId: string | number,
    payload: {description?: string; function?: string;},
    options?: {skipLoader?: boolean}
  ): Promise<ApiResponse<GroupItem>> {
    try {
      const url = GET_URL(ENDPOINTS.GROUP_DETAIL, {id: groupId});
      const baseHeaders = this.buildHeaders(options?.skipLoader) ?? new HttpHeaders();
      const headers = baseHeaders.set('Content-Type', 'application/merge-patch+json');
      const body: Record<string, string | null> = {};

      if (payload.description !== undefined) {
        body['description'] = payload.description?.trim() || null;
      }
      if (payload.function !== undefined) {
        body['function'] = payload.function?.trim() || null;
      }

      const response: any = await this.apiService.patch(url, body, {headers, json: false});
      return {success: true, result: this.mapGroup(response)};
    } catch (error: any) {
      log.error('Error updating group:', error);
      const message = this.extractValidationMessage(error);
      if (message) {
        return {success: false, message};
      }
    }
    return {success: false, message: 'No se pudo actualizar el grupo.'};
  }

  async setManager(groupId: string | number, employeeId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<undefined>> {
    try {
      const url = GET_URL(ENDPOINTS.GROUP_MANAGER, {id: groupId});
      const headers = this.buildHeaders(options?.skipLoader);
      await this.apiService.post(url, {employeeId}, headers ? {headers} : undefined);
      return {success: true};
    } catch (error: any) {
      log.error('Error setting group manager:', error);
      const message = this.extractValidationMessage(error);
      if (message) {
        return {success: false, message};
      }
    }
    return {success: false, message: 'No se pudo asignar el encargado.'};
  }

  async updateManagerAssignment(
    groupId: string | number,
    employeeId: string | number | null,
    options?: {skipLoader?: boolean}
  ): Promise<ApiResponse<GroupItem>> {
    try {
      const url = GET_URL(ENDPOINTS.GROUP_DETAIL, {id: groupId});
      const baseHeaders = this.buildHeaders(options?.skipLoader) ?? new HttpHeaders();
      const headers = baseHeaders.set('Content-Type', 'application/merge-patch+json');
      const body = {
        manager: employeeId ? this.buildIri('employees', employeeId) : null
      };
      const response: any = await this.apiService.patch(url, body, {headers, json: false});
      return {success: true, result: this.mapGroup(response)};
    } catch (error: any) {
      log.error('Error updating group manager assignment:', error);
    }
    return {success: false, message: 'No se pudo actualizar el responsable del grupo.'};
  }

  async getGroupCourses(groupId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<GroupCourse[]>> {
    try {
      const url = GET_URL(ENDPOINTS.GROUP_COURSES, {id: groupId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      const items = this.apiService.normalizeCollection<any>(response).member.map((item: any) => this.mapCourse(item));
      return {success: true, result: items};
    } catch (error: any) {
      log.error('Error loading group courses:', error);
    }
    return {success: false, message: 'No se pudieron cargar los cursos del grupo.'};
  }

  async getGroupPolicies(groupId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<GroupPolicy[]>> {
    try {
      const url = GET_URL(ENDPOINTS.GROUP_POLICIES, {id: groupId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      const items = this.apiService.normalizeCollection<any>(response).member.map((item: any) => this.mapPolicy(item));
      return {success: true, result: items};
    } catch (error: any) {
      log.error('Error loading group policies:', error);
    }
    return {success: false, message: 'No se pudieron cargar las políticas del grupo.'};
  }

  async getGroupNotes(groupId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<GroupNote[]>> {
    try {
      const url = GET_URL(ENDPOINTS.GROUP_NOTES, {id: groupId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      const items = this.apiService.normalizeCollection<any>(response).member.map((item: any) => this.mapNote(item));
      return {success: true, result: items};
    } catch (error: any) {
      log.error('Error loading group notes:', error);
    }
    return {success: false, message: 'No se pudieron cargar las notas del grupo.'};
  }

  async addGroupNote(groupId: string | number, content: string, options?: {skipLoader?: boolean}): Promise<ApiResponse<GroupNote>> {
    try {
      const url = GET_URL(ENDPOINTS.GROUP_NOTES, {id: groupId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.post(url, {content}, headers ? {headers} : undefined);
      return {success: true, result: this.mapNote(response)};
    } catch (error: any) {
      log.error('Error adding group note:', error);
    }
    return {success: false, message: 'No se pudo agregar la nota.'};
  }

  async deleteGroupNote(noteId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<undefined>> {
    try {
      const url = GET_URL(ENDPOINTS.NOTE_DETAIL, {id: noteId});
      const headers = this.buildHeaders(options?.skipLoader);
      await this.apiService.delete(url, headers ? {headers} : undefined);
      return {success: true};
    } catch (error: any) {
      log.error('Error deleting group note:', error);
    }
    return {success: false, message: 'No se pudo eliminar la nota.'};
  }

  async getGroupAttachments(groupId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<Attachment[]>> {
    try {
      const url = GET_URL(ENDPOINTS.GROUP_ATTACHMENTS, {id: groupId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      const items = this.apiService.normalizeCollection<any>(response).member.map((item: any) => this.mapAttachment(item));
      return {success: true, result: items};
    } catch (error: any) {
      log.error('Error loading group attachments:', error);
    }
    return {success: false, message: 'No se pudieron cargar los adjuntos del grupo.'};
  }

  private mapGroup(item: any): GroupItem {
    const employees = (item.employees || item.members || []).map((member: any) => this.mapMember(member));
    const manager = item.manager ? this.mapMember(item.manager) : employees.find((m: any) => m.isManager) || null;
    return {
      id: item.id,
      name: item.name,
      description: item.description,
      function: item.function || item.role,
      manager,
      employees
    };
  }

  private mapMember(member: any): GroupMember {
    const firstName = member.firstName || '';
    const lastName = member.lastName || '';
    const name = member.name || `${firstName} ${lastName}`.trim() || 'Sin nombre';
    return {
      id: member.id,
      name,
      isManager: member.isManager || member.manager || false
    };
  }

  private mapCourse(item: any): GroupCourse {
    return {
      id: item.id,
      name: item.name || 'Curso',
      employees: Number(item.employees || 0),
      completed: Number(item.completed || 0),
      progress: Number(item.progress || 0)
    };
  }

  private mapPolicy(item: any): GroupPolicy {
    return {
      id: item.id,
      name: item.name || 'Política',
      employees: Number(item.employees || 0),
      accepted: Number(item.accepted || 0),
      pending: Number(item.pending || 0),
      acceptanceRate: Number(item.acceptanceRate || 0)
    };
  }

  private mapNote(item: any): GroupNote {
    return {
      id: item.id,
      content: item.content || '',
      createdAt: item.createdAt || item.created_at,
      author: item.author ? {
        id: item.author.id,
        name: item.author.name
      } : {
        id: item.authorId,
        name: item.authorName
      }
    };
  }

  private mapAttachment(item: any): Attachment {
    return {
      '@context': item['@context'],
      '@id': item['@id'],
      '@type': item['@type'],
      id: item.id,
      fileName: item.fileName || item.name || item.filename || 'Documento',
      fileSize: item.fileSize || item.size || item.bytes,
      mimeType: item.mimeType || item.type,
      filePath: item.filePath || item.path,
      createdAt: item.createdAt || item.created_at
    };
  }

  private buildHeaders(skipLoader = false): HttpHeaders | null {
    if (this.authState.isAdmin()) {
      if (!skipLoader) return null;
      return new HttpHeaders().set('X-Skip-Loader', '1');
    }

    const companyId = this.companyState.membership()?.company?.id;
    if (!companyId) {
      if (!skipLoader) return null;
      return new HttpHeaders().set('X-Skip-Loader', '1');
    }

    let headers = new HttpHeaders().set('X-Selected-Company-ID', String(companyId));
    if (skipLoader) {
      headers = headers.set('X-Skip-Loader', '1');
    }
    return headers;
  }

  private buildIri(resource: string, id: string | number) {
    const prefix = CONFIG.API_PREFIX.startsWith('/') ? CONFIG.API_PREFIX : `/${CONFIG.API_PREFIX}`;
    const normalized = prefix.endsWith('/') ? prefix : `${prefix}/`;
    return `${normalized}${resource}/${id}`;
  }

  private extractValidationMessage(error: any): string {
    if (!error) return '';

    if (typeof error.message === 'string' && error.message.trim()) {
      return error.message.trim();
    }

    const details = error.details;
    if (Array.isArray(details)) {
      const firstViolation = details.find(item => item?.message);
      if (firstViolation?.propertyPath && firstViolation?.message) {
        return `${firstViolation.propertyPath}: ${firstViolation.message}`;
      }
      if (firstViolation?.message) {
        return String(firstViolation.message);
      }
    }

    if (typeof details?.message === 'string' && details.message.trim()) {
      return details.message.trim();
    }

    return '';
  }
}
